import React from 'react';
import type { FC } from 'react';
import { Box, Text, Link, useInput, Key } from '@wix/cli-ui-kit';
import { Trans, useTranslation } from '@wix/cli-i18n';
import { PlatformType } from '@wix/cli-app-definitions';
import { isType } from 'variant';
import { useSite } from '../../site/index.js';
import { ScreenState } from './screen-state.js';
import { PlatformSelector } from '../Platform/PlatformSelector.js';
import { useExtensions } from '../extensions/useExtensions.js';
import { DashboardNumericViewer } from './DashboardNumericViewer.js';
import { useWixCliAppBi } from '../../bi/index.js';
import { usePlatform } from '../Platform/usePlatform.js';
import { useBackendComponents } from '../../backend/useBackendComponents.js';

interface Props {
  onScreenChange: (screen: ScreenState) => void;
}

export const HomeScreen: FC<Props> = ({ onScreenChange }) => {
  const { t } = useTranslation();
  const { site } = useSite();
  const bi = useWixCliAppBi();
  const { hasSinglePlatform } = usePlatform();
  const { hasExtensions, dashboardExtensions } = useExtensions();
  const { backendComponents } = useBackendComponents();

  useInput(async (input) => {
    if (input === 'c') {
      bi.cliAction({
        question: t('dev_command.dev_site.change'),
        questionKey: 'dev_command.dev_site.change',
        action: 'change_site',
        key: input,
      });
      onScreenChange(ScreenState.SiteSelector());
    }
  });

  return (
    <>
      <Box gap={1}>
        <Trans
          i18nKey="dev_command.dev_site.current"
          values={{ siteName: site.displayName }}
          components={[<Text bold skin="info" />]}
        />

        <Text skin="secondary">
          <Trans
            i18nKey="dev_command.dev_site.change"
            components={[<Key value="C" skin="secondary" />]}
          />
        </Text>
      </Box>

      <Box paddingTop={1} paddingBottom={1} flexDirection="column">
        {!hasExtensions() ? (
          <Trans
            i18nKey="general.platform.none"
            // Original link is:
            // https://dev.wix.com/docs/build-apps/developer-tools/cli/get-started/about-the-wix-cli-for-apps
            components={[<Link url="https://wix.to/b7o4Ml0" />]}
          />
        ) : (
          <>
            {hasSinglePlatform(PlatformType.Dashboard()) &&
            backendComponents.length === 0 &&
            dashboardExtensions.some(
              (dashboardExtension) =>
                !isType(dashboardExtension, 'DashboardModal')
            ) ? (
              <DashboardNumericViewer
                onViewMore={(selectorType) =>
                  onScreenChange(
                    ScreenState.DashboardScreen({
                      selectorType,
                    })
                  )
                }
              />
            ) : (
              <>
                <Trans
                  i18nKey="general.view_changes"
                  components={[<Text skin="secondary" />]}
                />
                <PlatformSelector
                  onMultipleDashboardExtensionSelection={() =>
                    onScreenChange(ScreenState.DashboardScreen())
                  }
                  onMultipleBackendComponentSelection={() =>
                    onScreenChange(ScreenState.BackendScreen())
                  }
                />
              </>
            )}
          </>
        )}
      </Box>
    </>
  );
};
